<template>
  <Panel class="instagram-dm-test-code-panel">
    <div class="code-panel-header">
      <Icon :name="panelState === 'active' ? 'check-circle' : 'alert-circle-outline'" :color="headerColor" />
      <span class="status-text">{{ headerText }}</span>
    </div>

    <div class="code-panel-body">
      <code :class="['code-text', { 'code-text-disabled': panelState !== 'active' }]">{{ current.code }}</code>
      <el-button v-if="panelState === 'active'" size="small" @click="$emit('copy', current.code)">
        <i class="mdi mdi-content-copy" />
        コピー
      </el-button>
    </div>

    <div class="code-panel-meta">
      <span v-if="panelState === 'active'" :class="['lottery-result', current.lotteryResult]">
        {{ lotteryResultLabel }}
      </span>
      <span v-if="panelState === 'active'" class="remaining-time">
        <Icon name="clock-outline" size="small" />
        残り {{ remainingTimeText }}
      </span>
    </div>
  </Panel>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import Icon from '@/client/components/_atoms/Icon.vue'
import Panel from '@/client/components/_atoms/Panel.vue'
import { InstagramInstantwinPretestEntryCode } from '@/typings/api/instagram_instantwin_pretest_entry_codes.schema'

import { getInstagramDmTestLotteryResultLabel } from '../../utils/instagramDmTest'

interface Props {
  current: InstagramInstantwinPretestEntryCode
  panelState: 'active' | 'expired'
  disabled: boolean
  remainingMinutes: number
}

interface Emits {
  (e: 'copy', code: string): void
}

const props = defineProps<Props>()
defineEmits<Emits>()

const lotteryResultLabel = computed(() => getInstagramDmTestLotteryResultLabel(props.current.lotteryResult))
const remainingTimeText = computed(() => {
  const minutes = props.remainingMinutes
  if (minutes < 60) {
    return `${minutes} 分`
  }
  const hours = Math.floor(minutes / 60)
  const remainMinutes = minutes % 60
  return remainMinutes > 0 ? `${hours} 時間 ${remainMinutes} 分` : `${hours} 時間`
})
const headerText = computed(() =>
  props.panelState === 'active' ? 'テストコード発行済み' : 'テストコードの有効期限が切れました'
)
const headerColor = computed(() => (props.panelState === 'active' ? 'success' : 'warning'))
</script>

<style lang="scss" scoped>
@use '@/client/assets/scss/variables.scss' as *;

.instagram-dm-test-code-panel {
  margin-bottom: $spacing-unit * 2;
}

.code-panel-header {
  display: flex;
  gap: $spacing-unit;
  align-items: center;
  margin-bottom: $spacing-unit;
}

.status-text {
  font-weight: bold;
}

.code-panel-body {
  display: flex;
  gap: $spacing-unit;
  align-items: center;
  margin-bottom: $spacing-unit;
}

.code-text {
  flex: 1;
  padding: $spacing-unit;
  font-family: monospace;
  font-size: 14px;
  word-break: break-all;
  background-color: $background-color;
  border-radius: $round;
}

.code-text-disabled {
  color: $sub-text-color;
  text-decoration: line-through;
}

.code-panel-meta {
  display: flex;
  gap: $spacing-unit * 2;
  align-items: center;
}

.lottery-result {
  padding: 2px $spacing-unit;
  font-size: $font-small-size;
  font-weight: bold;
  border-radius: $round;

  &.win {
    color: $success-color;
    background-color: rgba($success-color, 0.1);
  }

  &.lose {
    color: $danger-color;
    background-color: rgba($danger-color, 0.1);
  }
}

.remaining-time {
  display: flex;
  gap: 4px;
  align-items: center;
  font-size: $font-small-size;
  color: $sub-text-color;
}

.code-panel-note {
  margin-top: $spacing-unit * 2;
}
</style>
